import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Users, Target, Clock, Trophy, AlertTriangle, BarChart3 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsView() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/pipeline/reports?days=${days}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !data) return <p className="text-sm text-muted-foreground">Lade Reports...</p>;

  const maxFunnel = Math.max(...data.stage_funnel.map(s => s.count_ever), 1);

  return (
    <div className="space-y-6" data-testid="reports-view">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Sales-Reports</p>
          <h2 className="font-heading text-xl font-black tracking-tight">Performance-Übersicht</h2>
        </div>
        <Select value={String(days)} onValueChange={v => setDays(parseInt(v, 10))}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="reports-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Letzte 7 Tage</SelectItem>
            <SelectItem value="30">Letzte 30 Tage</SelectItem>
            <SelectItem value="90">Letzte 90 Tage</SelectItem>
            <SelectItem value="365">Letztes Jahr</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Leads" value={data.total_leads} />
        <Stat icon={Trophy} label="Sales-Cycle Ø" value={data.avg_sales_cycle_days != null ? `${data.avg_sales_cycle_days} Tage` : "—"} />
        <Stat icon={Target} label="Aktive Vertriebler" value={data.user_performance.length} />
        <Stat icon={AlertTriangle} label="Top-Lost" value={data.top_lost_reasons[0]?.reason || "—"} small={!!data.top_lost_reasons[0]} />
      </div>

      {/* Funnel */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={14} className="text-muted-foreground" />
          <h3 className="font-heading font-bold text-sm uppercase tracking-widest">Pipeline-Funnel</h3>
        </div>
        <div className="space-y-2">
          {data.stage_funnel.map(s => (
            <div key={s.stage_id} className="flex items-center gap-3" data-testid={`funnel-${s.stage_id}`}>
              <div className="w-32 shrink-0 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="text-xs font-semibold truncate">{s.stage_name}</span>
              </div>
              <div className="flex-1 bg-muted rounded-sm h-7 relative overflow-hidden">
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${(s.count_ever / maxFunnel) * 100}%`, background: s.color, opacity: 0.85 }}
                />
                <span className="absolute inset-0 flex items-center px-3 text-xs font-mono font-bold text-foreground">
                  {s.count_ever} {s.count_now !== s.count_ever && <span className="text-muted-foreground ml-2">aktuell {s.count_now}</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-User-Perf + Lost-Reasons */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={14} className="text-muted-foreground" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-widest">Vertriebler-Performance</h3>
          </div>
          {data.user_performance.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Daten.</p>}
          <div className="space-y-2">
            {data.user_performance.map(u => (
              <div key={u.user_id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0" data-testid={`user-perf-${u.user_id}`}>
                <div className="min-w-0 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] font-bold shrink-0">
                    {u.name[0].toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {u.leads_total} Leads · {u.calls} Anrufe · Win-Rate {u.win_rate}%
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">{u.leads_won}W · {u.leads_lost}L</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-muted-foreground" />
            <h3 className="font-heading font-bold text-sm uppercase tracking-widest">Top Verlust-Gründe</h3>
          </div>
          {data.top_lost_reasons.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Verlust-Gründe erfasst.</p>}
          <div className="space-y-2">
            {data.top_lost_reasons.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm text-foreground">{r.reason}</span>
                <span className="text-xs font-mono font-bold text-muted-foreground">{r.count}×</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, small }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={11} className="text-muted-foreground" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
      </div>
      <p className={`font-heading font-black tracking-tight ${small ? "text-sm leading-tight" : "text-2xl"}`}>{value}</p>
    </div>
  );
}
